// import { EnhancedUser } from "../hooks/useAuth";

// Define all permission IDs in one central location
export const PERMISSIONS = {
  // Dashboard permissions
  ACCESS_DASHBOARD: 1,
  ACCESS_SALES_ANALYSIS: 17,
  ACCESS_REPORT_ANALYSIS: 16,

  // Table permissions
  VIEW_TABLES: 18,
  ADD_TABLES: 19,
  MODIFY_TABLES: 20,
  DELETE_TABLES: 21,

  // Order permissions
  ACCESS_ORDERS: 22,
  CREATE_ORDERS: 23,
  MODIFY_ORDERS: 24,
  DELETE_ORDERS: 25,
  PROCESS_PAYMENTS: 27,
  VIEW_ALL_USERS_ORDERS: 28,
  VIEW_ORDERS_DETAILS: 49,
  UPDATE_ORDER_STATUS: 50,

  // Roles & permissions
  VIEW_ROLES: 29,
  CREATE_ROLES: 30,
  MODIFY_ROLES: 31,
  MODIFY_PERMISSIONS: 53,
  DELETE_ROLES: 32,

  // User management
  VIEW_USERS: 33,
  ADD_USERS: 34,
  MODIFY_USERS: 35,
  DELETE_USERS: 36,

  // Location management
  VIEW_LOCATIONS: 54,
  ADD_LOCATIONS: 55,
  MODIFY_LOCATIONS: 56,
  DELETE_LOCATIONS: 57,
  CAN_SEE_ALL_LOCATIONS: 58,

  // Inventory
  VIEW_INVENTORY: 37,
  ADD_INVENTORY: 38,
  MODIFY_INVENTORY: 39,
  DELETE_INVENTORY: 40,

  //Promotions
  POST_PROMOTIONS: 52,

  // Vendors
  VIEW_VENDORS: 41,
  ADD_VENDORS: 42,
  MODIFY_VENDORS: 43,
  DELETE_VENDORS: 44,

  // Scheduling
  VIEW_SCHEDULES: 45,
  CREATE_SCHEDULES: 46,
  MODIFY_SCHEDULES: 47,
  DELETE_SCHEDULES: 48,
  CAN_SEE_ALL_SCHEDULES: 59,
};

// Helper function to safely get user permissions
const getUserPermissions = (
  user: any
): { permission_id: number; status: number }[] => {
  // Based on your actual user structure from the login response
  if (!user) return [];

  // Case 1: Access via user.roles.permissions (from your login response)
  if (user.roles && Array.isArray(user.roles.permissions)) {
    return user.roles.permissions;
  }

  // Case 2: Access via user.roleInfo.role.permissions (based on useAuth.ts)
  if (
    user.roleInfo &&
    user.roleInfo.role &&
    Array.isArray(user.roleInfo.role.permissions)
  ) {
    return user.roleInfo.role.permissions;
  }

  // Case 3: Handle case where permissions might be directly in roleInfo
  if (user.roleInfo && user.roleInfo.permissions) {
    // If permissions is already a map of permission_id -> boolean
    if (
      typeof user.roleInfo.permissions === "object" &&
      !Array.isArray(user.roleInfo.permissions)
    ) {
      // Convert the permissions map to an array of permission objects
      const permissionsArray = [];
      for (const permId in user.roleInfo.permissions) {
        if (user.roleInfo.permissions.hasOwnProperty(permId)) {
          permissionsArray.push({
            permission_id: Number(permId),
            status: user.roleInfo.permissions[permId] ? 1 : 0,
          });
        }
      }
      return permissionsArray;
    }
  }

  // Fallback to empty array if no permissions are found
  return [];
};

// Check if a user has ANY of the specified permissions
export const hasAnyPermission = (
  user: any,
  requiredPermissionIds: number[]
): boolean => {
  if (!user) return false;

  // Get all of the user's active permissions
  const userPermissions = getUserPermissions(user)
    .filter((permission) => permission.status === 1)
    .map((permission) => permission.permission_id);

  // Check if the user has ANY of the required permissions
  return requiredPermissionIds.some((permId) =>
    userPermissions.includes(permId)
  );
};

// Check if a user has ALL of the specified permissions
export const hasAllPermissions = (
  user: any,
  requiredPermissionIds: number[]
): boolean => {
  if (!user) return false;

  // Get all of the user's active permissions
  const userPermissions = getUserPermissions(user)
    .filter((permission) => permission.status === 1)
    .map((permission) => permission.permission_id);

  // Check if the user has ALL of the required permissions
  return requiredPermissionIds.every((permId) =>
    userPermissions.includes(permId)
  );
};

// Check if a user has a specific permission
export const hasPermission = (user: any, permissionId: number): boolean => {
  return hasAnyPermission(user, [permissionId]);
};
